/**
 * PA — Strategy E-CFG platform defaults (F4).
 *
 * Edits chaining defaults, dismiss interval, hold cap, and declared maxima.
 * Writes go through /api/admin/strategy/config (privileged path) — tenants
 * cannot write platform rows via growth_os_app_role.
 */
import { useCallback, useEffect, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import {
  api,
  type EffectiveConfig,
  type PlatformConfig,
} from '@/api/client'
import { useToast } from '@/components/ui/toast'
import { usePageTitle } from '@/lib/usePageTitle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface FormState {
  dismissHours: string
  maxDismissHours: string
  holdCapDays: string
  defaultChained: boolean
}

function hoursFromSeconds(seconds: unknown): string {
  const n = Number(seconds)
  if (!Number.isFinite(n)) return ''
  return String(n / 3600)
}

function secondsFromHours(hours: string): number | null {
  const n = Number(hours)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 3600)
}

function formFromPlatform(platform: PlatformConfig | null): FormState {
  return {
    dismissHours: hoursFromSeconds(platform?.dismiss_interval?.seconds ?? 14400),
    maxDismissHours: hoursFromSeconds(platform?.declared_maxima?.dismiss_interval_seconds ?? 86400),
    holdCapDays: String(platform?.deadline_policies?.hold_cap_days ?? 7),
    defaultChained: platform?.chaining_rules?.default_chained !== false,
  }
}

function originLabel(origin: string | undefined): string {
  if (origin === 'platform') return 'platform default'
  if (origin === 'tenant') return 'agency override'
  if (origin === 'plan') return 'this plan'
  return origin || '—'
}

export function StrategyConfigAdminPage() {
  usePageTitle('Strategy config')
  const { addToast } = useToast()
  const [platform, setPlatform] = useState<PlatformConfig | null>(null)
  const [effective, setEffective] = useState<EffectiveConfig | null>(null)
  const [form, setForm] = useState<FormState>(formFromPlatform(null))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getAdminStrategyConfig()
      setPlatform(res.platform)
      setEffective(res.effective)
      setForm(formFromPlatform(res.platform))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load strategy config')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save() {
    const dismissSeconds = secondsFromHours(form.dismissHours)
    const maxDismissSeconds = secondsFromHours(form.maxDismissHours)
    const holdCapDays = Number(form.holdCapDays)
    if (dismissSeconds == null || maxDismissSeconds == null) {
      addToast({ title: 'Dismiss intervals must be non-negative numbers', variant: 'error' })
      return
    }
    if (!Number.isFinite(holdCapDays) || holdCapDays < 0) {
      addToast({ title: 'Hold cap must be a non-negative number of days', variant: 'error' })
      return
    }
    if (dismissSeconds > maxDismissSeconds) {
      addToast({
        title: 'Default dismiss cannot exceed the declared maximum',
        variant: 'error',
      })
      return
    }

    setSaving(true)
    try {
      const res = await api.putAdminStrategyConfig({
        chaining_rules: {
          ...(platform?.chaining_rules || {}),
          default_chained: form.defaultChained,
        },
        deadline_policies: {
          ...(platform?.deadline_policies || {}),
          hold_cap_days: holdCapDays,
        },
        dismiss_interval: {
          ...(platform?.dismiss_interval || {}),
          seconds: dismissSeconds,
        },
        declared_maxima: {
          ...(platform?.declared_maxima || {}),
          dismiss_interval_seconds: maxDismissSeconds,
        },
        default_intervals: platform?.default_intervals || {},
        lead_times: platform?.lead_times || {},
        approval_targets: platform?.approval_targets || {},
      })
      setPlatform(res.platform)
      setForm(formFromPlatform(res.platform))
      addToast({ title: 'Strategy platform defaults saved.', variant: 'success' })
      await load()
    } catch (err) {
      addToast({
        title: 'Could not save strategy config',
        description: err instanceof Error ? err.message : 'Try again',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const dismissOrigin = effective?.values?.dismiss_interval?.origin

  return (
    <div className="mx-auto max-w-3xl px-[var(--lc-space-md)] py-[var(--lc-space-xl)]">
      <div className="mb-[var(--lc-space-xl)] flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[length:var(--lc-type-heading-1)] font-semibold text-[var(--lc-text-heading)]">
            Strategy config
          </h1>
          <p className="mt-1 text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
            Platform defaults for chaining, dismiss re-ring, and declared maxima.
            Tenant overrides may narrow these values but never exceed a maximum.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} className="gap-1.5">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-text-muted)]" />
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-[var(--lc-radius-md)] border border-[var(--lc-status-unpublished-fg)] bg-[var(--lc-status-unpublished-bg)] px-3 py-2 text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]"
        >
          {error}
        </div>
      ) : (
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <section className="space-y-4 rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface-raised)] p-4">
            <div>
              <h2 className="font-semibold text-[var(--lc-text-heading)]">Chaining</h2>
              <p className="mt-0.5 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                D-66 — chained is the safe default when dependencies are not mechanically clear.
              </p>
            </div>
            <label className="flex items-center gap-3 text-[length:var(--lc-type-body-sm)]">
              <input
                type="checkbox"
                checked={form.defaultChained}
                onChange={(e) => setForm((prev) => ({ ...prev, defaultChained: e.target.checked }))}
                className="h-4 w-4"
              />
              Default milestones to chained
            </label>
          </section>

          <section className="space-y-4 rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface-raised)] p-4">
            <div>
              <h2 className="font-semibold text-[var(--lc-text-heading)]">Dismiss interval</h2>
              <p className="mt-0.5 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                D-65 — default re-ring gap after dismiss (4 hours). Agency may override within the
                maximum. A re-ring past the item deadline is clamped, never rejected.
                {dismissOrigin ? (
                  <>
                    {' '}
                    Effective origin: {originLabel(dismissOrigin)}.
                  </>
                ) : null}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="dismiss-hours">Default (hours)</Label>
                <Input
                  id="dismiss-hours"
                  type="number"
                  min={0}
                  step="0.25"
                  value={form.dismissHours}
                  onChange={(e) => setForm((prev) => ({ ...prev, dismissHours: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="max-dismiss-hours">Declared maximum (hours)</Label>
                <Input
                  id="max-dismiss-hours"
                  type="number"
                  min={0}
                  step="0.25"
                  value={form.maxDismissHours}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxDismissHours: e.target.value }))}
                />
              </div>
            </div>
          </section>

          <section className="space-y-4 rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface-raised)] p-4">
            <div>
              <h2 className="font-semibold text-[var(--lc-text-heading)]">Deadline hold cap</h2>
              <p className="mt-0.5 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                Days past delivery before escalation (platform baseline; D-62 closed at 7).
              </p>
            </div>
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="hold-cap-days">Hold cap (days)</Label>
              <Input
                id="hold-cap-days"
                type="number"
                min={0}
                step={1}
                value={form.holdCapDays}
                onChange={(e) => setForm((prev) => ({ ...prev, holdCapDays: e.target.value }))}
              />
            </div>
          </section>

          {platform?.set_at ? (
            <p className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
              Last set {new Date(platform.set_at).toLocaleString()}
              {platform.set_by ? ` by ${platform.set_by}` : ''}.
            </p>
          ) : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                'Save platform defaults'
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
